import React, { Children, useLayoutEffect, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

// Keep existing card JSX and CSS columns; TanStack mounts only nearby rows.
export default function VirtualGallery({ children, className, singleColumn = false }) {
  const container = useRef(null);
  const [layout, setLayout] = useState({ columns: 1, gap: 16, margin: 0 });
  const all = Children.toArray(children);
  const footer = all.filter(child => child.props?.['aria-hidden']);
  const cards = all.filter(child => !child.props?.['aria-hidden']);
  useLayoutEffect(() => {
    const element = container.current;
    const measure = () => {
      const style = getComputedStyle(element);
      const columns = singleColumn ? 1 : style.gridTemplateColumns.split(' ').filter(Boolean).length;
      const gap = parseFloat(style.rowGap) || 0;
      const margin = element.getBoundingClientRect().top + window.scrollY;
      setLayout(prev => prev.columns === columns && prev.gap === gap && prev.margin === margin
        ? prev : { columns: Math.max(1, columns), gap, margin });
    };
    measure();
    const observer = new ResizeObserver(measure);
    // Observe ancestors too: generation forms above the gallery can expand.
    let ancestor = element;
    while (ancestor && ancestor !== document.documentElement) { observer.observe(ancestor); ancestor = ancestor.parentElement; }
    window.addEventListener('resize', measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, [singleColumn]);
  const virtualizer = useWindowVirtualizer({
    count: Math.ceil(cards.length / layout.columns),
    estimateSize: () => singleColumn ? 180 : 650,
    overscan: 2,
    gap: layout.gap,
    scrollMargin: layout.margin,
    getItemKey: index => cards[index * layout.columns]?.key || index,
  });
  useLayoutEffect(() => { virtualizer.measure(); }, [layout.columns, virtualizer]);
  return (
    <div ref={container} className={className}>
      <div style={{ gridColumn: '1 / -1', position: 'relative', width: '100%', height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map(row => (
          <div key={row.key} data-index={row.index} ref={virtualizer.measureElement}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', display: 'grid',
              gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`, gap: layout.gap,
              transform: `translateY(${row.start - layout.margin}px)` }}>
            {cards.slice(row.index * layout.columns, (row.index + 1) * layout.columns)}
          </div>
        ))}
      </div>
      {footer}
    </div>
  );
}
